import { NavLink } from 'react-router-dom';
import { MODULES, NAV_GROUP_ORDER } from '@/config/modules.config';
import { isModuleEnabled } from '@/config/featureFlags';
import { useI18n } from '@/i18n';
import { Icon } from './Icon';
import { RecentlyViewed } from './RecentlyViewed';
import { Footer } from './Footer';

/**
 * Reads MODULES + FEATURE_FLAGS and nothing else. This file has not
 * changed, and does not need to change, when the Partnerships module was
 * added above — proving the "add module without touching Sidebar" claim.
 * Phase C reorganized the flat live/coming-next list into the fixed
 * NAV_GROUP_ORDER sections (Command/Operations/Growth/Business/AI &
 * Automation/System) — still purely a read of MODULES' `navGroup` field,
 * so adding a module still never means touching this file.
 *
 * `open`/`onClose` turn this into an off-canvas drawer below the mobile
 * breakpoint (see global.css's `.sidebar` media query) — same markup,
 * same MODULES-driven content, on desktop it's simply always visible and
 * these props are unused there. Clicking a link or the backdrop closes
 * the drawer on mobile so navigating never leaves it covering the page.
 */
export function Sidebar({ open = false, onClose }: { open?: boolean; onClose?: () => void }) {
  const { t } = useI18n();

  return (
    <>
      {open && <div className="sidebar-backdrop" onClick={onClose} aria-hidden="true" />}
      <aside className={`sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-mobile-head">
          <div className="brand">UGARITS COMMAND</div>
          <button className="icon-btn sidebar-close" onClick={onClose} aria-label={t('common.close')}>
            <Icon name="x" />
          </button>
        </div>

        {NAV_GROUP_ORDER.map(({ key, labelKey }) => {
          const items = MODULES.filter((m) => m.navGroup === key).sort((a, b) => a.order - b.order);
          if (items.length === 0) return null;
          return (
            <div key={key}>
              <div className="group-label">{t(labelKey)}</div>
              {items.map((m) =>
                m.group === 'live' && isModuleEnabled(m.featureFlag) ? (
                  <NavLink key={m.id} to={m.route} onClick={onClose} className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}>
                    <Icon name={m.icon} /> {t(m.labelKey)}
                  </NavLink>
                ) : m.group === 'live' ? (
                  <div key={m.id} className="nav-link disabled">
                    <span><Icon name={m.icon} /> {t(m.labelKey)}</span>
                    <span className="soon-tag">{t('common.comingSoon')}</span>
                  </div>
                ) : (
                  <NavLink key={m.id} to={`/${m.id}`} onClick={onClose} className={({ isActive }) => `nav-link soon-link ${isActive ? 'active' : ''}`} title={t('comingSoon.explanation')}>
                    <span><Icon name={m.icon} /> {t(m.labelKey)}</span>
                    <span className="soon-tag">{t('common.comingSoon')}</span>
                  </NavLink>
                )
              )}
            </div>
          );
        })}

        <RecentlyViewed />
        <Footer variant="sidebar" />
      </aside>
    </>
  );
}
